import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { useUpdateNonProfitOrg, useUpdateNonProfitOrgWiki } from '@/api/hooks/nonProfitOrgHooks';
import NpoPartyAuditForm from '@/components/forms/NpoPartyAuditForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { ThirdPartyAuditData } from '@/lib/validations/NpoOnboardingSchema';
import { containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { FileToUpload } from '@/utils/uploadMultipleToFireStorage';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';

const NpoPartyAudit = () => {
  const { thirdPartyAudit, thirdPartyAuditDeny, npoId } = useLocalSearchParams<{
    thirdPartyAudit: string;
    thirdPartyAuditDeny: string;
    npoId: string;
  }>();
  const { state: authState } = useAuth();
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();
  const { mutateAsync: nonProfitUpdateWiki, isPending: nonProfitIsPendingWiki } = useUpdateNonProfitOrgWiki();

  const { showSuccess, showError } = useEndFlowModal();
  const { setVisible, setNpoId } = useWikiChangesNpoModal();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const initialData = {
    thirdPartyAudit: typeof thirdPartyAudit === 'string' ? safeJsonParseArray(thirdPartyAudit)[0] : '',
    thirdPartyAuditDeny: typeof thirdPartyAuditDeny === 'string' ? thirdPartyAuditDeny === 'true' : thirdPartyAuditDeny
  };

  const updateNonProfit = ({
    thirdPartyAudit,
    thirdPartyAuditDeny
  }: {
    thirdPartyAudit: string[] | null;
    thirdPartyAuditDeny: boolean;
  }) => {
    if (authState.authReady && authState.user?.user.role === 'INDIVIDUAL') {
      nonProfitUpdateWiki(
        {
          npoId: npoId ?? '',
          thirdPartyAudit: thirdPartyAudit,
          thirdPartyAuditDeny: thirdPartyAuditDeny
        },
        {
          onSuccess: () => {
            setIsLoading(false);
            setFileProgress(undefined);
            setNpoId(npoId ?? '');
            setVisible(true);
          },
          onError: (e) => {
            setIsLoading(false);
            setFileProgress(undefined);
            showError({ message: e.message });
            console.error('Error', e);
          }
        }
      );
    } else {
      nonProfitUpdate(
        {
          npoId: authState.signUpData?.loginEmail?.npoId ?? '',
          thirdPartyAudit: thirdPartyAudit,
          thirdPartyAuditDeny: thirdPartyAuditDeny
        },
        {
          onSuccess: () => {
            setIsLoading(false);
            setFileProgress(undefined);
            showSuccess({
              title: i18n.t('end_flow.success_title'),
              message: i18n.t('npo_details.update_message')
            });
            router.back();
          },
          onError: (e) => {
            setIsLoading(false);
            setFileProgress(undefined);
            showError({ message: e.message });
            console.error('Error', e);
          }
        }
      );
    }
  };

  const onSubmit: SubmitHandler<ThirdPartyAuditData> = async (data) => {
    try {
      setIsLoading(true);
      if (data.thirdPartyAudit && !containsSection(data.thirdPartyAudit, 'thirdParty')) {
        let fileToUpload: FileToUpload = {
          uri: data.thirdPartyAudit,
          name: `third_party_audit.${new Date().toISOString()}`
        };
        await uploadToFireStorage(
          `non-profits-orgs/${npoId ? npoId : authState.signUpData?.loginEmail?.npoId}/thirdParty/`,
          fileToUpload,
          (progress) => setFileProgress(progress)
        ).then((storageFile) => {
          updateNonProfit({
            thirdPartyAudit: [storageFile.metadata.fullPath],
            thirdPartyAuditDeny: data.thirdPartyAuditDeny
          });
        });
      } else {
        if (data.thirdPartyAuditDeny) {
          updateNonProfit({
            thirdPartyAudit: null,
            thirdPartyAuditDeny: data.thirdPartyAuditDeny
          });
        } else {
          updateNonProfit({
            thirdPartyAudit: [],
            thirdPartyAuditDeny: data.thirdPartyAuditDeny
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  return (
    <NpoPartyAuditForm
      initialData={initialData}
      isLoading={nonProfitIsPending || nonProfitIsPendingWiki || isLoading}
      showBackButton={false}
      showSaveAndContinueButton={false}
      submitLabel={i18n.t('mix.done')}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default NpoPartyAudit;
